<template>
  <div class="dashboard">
    <p class="message">📊 Progreso del grupo</p>
    <p class="submessage">
      <span v-if="groupName">{{ groupName }}</span>
      <span v-else>Grupo</span>
      <span> · {{ monthLabel }}</span>
    </p>

    <div class="cards">
      <div class="card card-feature">
        <div class="card-title">Grupo</div>
        <div class="feature-main">{{ groupName || "Sin nombre" }}</div>
      </div>

      <div class="card">
        <div class="card-title">Estudiantes</div>
        <div class="kpis single">
          <div class="kpi">
            <span>En el grupo</span>
            <b>{{ rawStudentsCount }}</b>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-title">Sesiones del mes</div>
        <div class="kpis single">
          <div class="kpi">
            <span>Total</span>
            <b>{{ totals.sessions }}</b>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-title">Completadas del mes</div>
        <div class="kpis single">
          <div class="kpi">
            <span>Total</span>
            <b>{{ totals.completed }}</b>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-title">Tiempo total</div>
        <div class="kpis single">
          <div class="kpi">
            <span>Acumulado</span>
            <b>{{ fmtMs(totals.totalMs) }}</b>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-title">Días activos</div>
        <div class="kpis single">
          <div class="kpi">
            <span>Suma del grupo</span>
            <b>{{ totals.activeDays }}</b>
          </div>
        </div>
      </div>
    </div>

    <div class="sections">
      <div class="section">
        <div class="section-head">
          <h3 class="section-title">Comparativa por estudiante</h3>
          <div class="legend">
            <span class="dot dot-completed"></span>
            <span>Completadas por estudiante</span>
          </div>
        </div>

        <div v-if="groupedList.length" ref="compareWrap" class="compare-wrap">
          <canvas
            ref="compareCanvas"
            class="compare-canvas"
            aria-label="Gráfico de completadas por estudiante"
            role="img"
          ></canvas>
        </div>

        <div v-else-if="!loading" class="empty-block">
          No hay estudiantes o datos mensuales para mostrar.
        </div>

        <div v-if="loading" class="skeleton-chart"></div>
      </div>

      <div class="section">
        <div class="section-head">
          <h3 class="section-title">Resumen por estudiante</h3>
          <small class="muted">Agrupado por nombre</small>
        </div>

        <ul v-if="groupedList.length" class="students-list">
          <li
            v-for="student in groupedList"
            :key="student.name"
            class="student-row"
          >
            <div class="student-main">
              <div class="student-avatar">{{ initials(student.name) }}</div>

              <div class="student-info">
                <div class="student-name">{{ student.name }}</div>
                <small class="muted">
                  {{ studentMeta(student) }}
                </small>
              </div>
            </div>

            <div class="student-kpis">
              <span>Sesiones: <b>{{ student.sessions }}</b></span>
              <span>Completadas: <b>{{ student.completed }}</b></span>
              <span>Días activos: <b>{{ student.activeDays }}</b></span>
              <span>Tiempo: <b>{{ fmtMs(student.totalMs) }}</b></span>
            </div>
          </li>
        </ul>

        <div v-else-if="!loading" class="empty-block">
          No hay estudiantes para mostrar.
        </div>
      </div>

      <div class="section">
        <div class="section-head">
          <h3 class="section-title">Categorías del grupo</h3>
          <small class="muted">Agrupado por tipo de nivel del mes</small>
        </div>

        <div class="cats-row" v-if="categoriesList.length">
          <button
            v-for="cat in categoriesList"
            :key="cat.key"
            type="button"
            class="cat-chip"
            :class="{ active: activeCat === cat.key }"
            :aria-pressed="activeCat === cat.key"
            :title="cat.title"
            @click="toggleCat(cat.key)"
          >
            <span class="cat-emoji" aria-hidden="true">{{ cat.emoji }}</span>
            <span class="cat-title">{{ cat.title }}</span>
            <span class="cat-metrics">
              <b>{{ cat.totals.completed }}</b> comp • {{ cat.totals.sessions }} ses
            </span>
          </button>
        </div>

        <div v-if="activeCategory" class="category-block">
          <div class="category-head">
            <div class="category-name">
              <span class="cat-emoji big" aria-hidden="true">{{ activeCategory.emoji }}</span>
              <h4>{{ activeCategory.title }}</h4>
            </div>

            <div class="category-totals">
              <span>Sesiones: <b>{{ activeCategory.totals.sessions }}</b></span>
              <span>Completadas: <b>{{ activeCategory.totals.completed }}</b></span>
              <span>Tiempo: <b>{{ fmtMs(activeCategory.totals.totalMs) }}</b></span>
            </div>
          </div>

          <ul class="levels">
            <li
              v-for="lvl in activeCategory.levels"
              :key="lvl.id"
              class="level-row"
            >
              <div class="level-title">
                {{ lvl.title || lvl.id }}
                <small class="muted" v-if="lvl.subtitle"> · {{ lvl.subtitle }}</small>
              </div>

              <div class="level-kpis">
                <span>Sesiones: <b>{{ lvl.sessions }}</b></span>
                <span>Completadas: <b>{{ lvl.completed }}</b></span>
                <span>Tiempo: <b>{{ fmtMs(lvl.totalMs) }}</b></span>
              </div>
            </li>
          </ul>
        </div>

        <div v-else-if="!loading" class="empty-block">
          No hay categorías para mostrar.
        </div>
      </div>
    </div>

    <div v-if="!loading && !error && !groupedList.length" class="empty-state">
      No hay datos del grupo para este mes.
    </div>

    <div v-if="error" class="error">
      {{ error }}
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import { getAuth, onAuthStateChanged } from "firebase/auth";

export default {
  name: "GroupDashboardStats",

  props: {
    userId: { type: String, default: "" },
    month: { type: String, default: "" }
  },

  data() {
    return {
      dpr:
        typeof window !== "undefined"
          ? Math.min(window.devicePixelRatio || 1, 2)
          : 1,
      resizeHandler: null,
      ro: null,
      rafId: 0,
      hoverIndex: -1,
      bars: [],
      activeCat: "",
      authUnsub: null,
      resolvedUid: "",
      boundCanvas: null,
      boundWrap: null
    };
  },

  computed: {
    ...mapState("groupDashboardStats", {
      loading: (state) => state.loading,
      error: (state) => state.error,
      groupId: (state) => state.groupId,
      groupName: (state) => state.groupName,
      storeMonth: (state) => state.month,
      students: (state) => state.students,
      studentRows: (state) => state.studentRows
    }),

    ...mapGetters("groupDashboardStats", [
      "groupTotals",
      "groupedStudents",
      "topLevelsMonth",
      "hasData"
    ]),

    totals() {
      return this.groupTotals || {
        activeDays: 0,
        sessions: 0,
        completed: 0,
        totalMs: 0
      };
    },

    groupedList() {
      return Array.isArray(this.groupedStudents) ? this.groupedStudents : [];
    },

    allLevels() {
      return Array.isArray(this.topLevelsMonth) ? this.topLevelsMonth : [];
    },

    rawStudentsCount() {
      return Array.isArray(this.students) ? this.students.length : 0;
    },

    locale() {
      return "es-ES";
    },

    monthLabel() {
      const raw = this.storeMonth || this.month;
      if (!raw || !/^\d{4}-\d{2}$/.test(raw)) return "Mes actual";

      const [year, month] = raw.split("-");
      const date = new Date(Number(year), Number(month) - 1, 1);

      if (Number.isNaN(date.getTime())) return raw;

      return date.toLocaleDateString(this.locale, {
        year: "numeric",
        month: "long"
      });
    },

    chartRows() {
      return this.groupedList.map((item) => ({
        label: item.name || "Sin nombre",
        value: Number(item.completed || 0),
        sessions: Number(item.sessions || 0),
        completed: Number(item.completed || 0),
        activeDays: Number(item.activeDays || 0),
        totalMs: Number(item.totalMs || 0),
        kidIds: Array.isArray(item.kidIds)
          ? item.kidIds
          : Array.isArray(item.studentIds)
            ? item.studentIds
            : []
      }));
    },

    categoriesMap() {
      const map = new Map();

      const ensure = (key, title, emoji) => {
        if (!map.has(key)) {
          map.set(key, {
            key,
            title,
            emoji,
            totals: { sessions: 0, completed: 0, totalMs: 0 },
            levels: []
          });
        }
        return map.get(key);
      };

      this.allLevels.forEach((lvl) => {
        const cat = this.inferCategory(lvl);
        const bucket = ensure(cat.key, cat.title, cat.emoji);

        const sessions = Number(lvl.sessions || 0);
        const completed = Number(lvl.completed || 0);
        const totalMs = Number(lvl.totalMs || 0);

        bucket.totals.sessions += sessions;
        bucket.totals.completed += completed;
        bucket.totals.totalMs += totalMs;

        bucket.levels.push({
          id: lvl.id,
          title: lvl.title || lvl.id,
          subtitle: lvl.subtitle || "",
          sessions,
          completed,
          totalMs
        });
      });

      for (const bucket of map.values()) {
        bucket.levels.sort((a, b) => {
          if (b.completed !== a.completed) return b.completed - a.completed;
          if (b.sessions !== a.sessions) return b.sessions - a.sessions;
          return b.totalMs - a.totalMs;
        });
      }

      return map;
    },

    categoriesList() {
      const arr = Array.from(this.categoriesMap.values());

      arr.sort((a, b) => {
        if (b.totals.completed !== a.totals.completed) {
          return b.totals.completed - a.totals.completed;
        }
        if (b.totals.sessions !== a.totals.sessions) {
          return b.totals.sessions - a.totals.sessions;
        }
        return b.totals.totalMs - a.totals.totalMs;
      });

      return arr;
    },

    activeCategory() {
      if (!this.categoriesList.length) return null;
      const current = this.activeCat || this.categoriesList[0]?.key;
      return this.categoriesList.find((c) => c.key === current) || this.categoriesList[0];
    }
  },

  watch: {
    userId: {
      immediate: false,
      handler(newVal) {
        if (newVal && newVal !== this.resolvedUid) {
          this.resolvedUid = newVal;
          this.fetchDashboard(newVal);
        }
      }
    },

    month() {
      if (this.resolvedUid) {
        this.fetchDashboard(this.resolvedUid);
      }
    },

    groupedList() {
      this.$nextTick(() => {
        this.setupCanvasBindings();
        this.scheduleDraw();
      });
    },

    categoriesList: {
      handler(list) {
        if (!list?.length) {
          this.activeCat = "";
          return;
        }

        if (!this.activeCat || !list.find((c) => c.key === this.activeCat)) {
          this.activeCat = list[0].key;
        }
      },
      immediate: true
    }
  },

  mounted() {
    this.setupAuthAndLoad();

    if (typeof window !== "undefined") {
      this.resizeHandler = () => this.scheduleDraw();
      window.addEventListener("resize", this.resizeHandler);
    }

    this.$nextTick(() => {
      this.setupCanvasBindings();
      this.scheduleDraw();
    });
  },

  beforeUnmount() {
    if (typeof this.authUnsub === "function") {
      this.authUnsub();
      this.authUnsub = null;
    }

    if (typeof window !== "undefined" && this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
    }

    if (this.ro) {
      this.ro.disconnect();
      this.ro = null;
    }

    if (this.rafId && typeof window !== "undefined") {
      window.cancelAnimationFrame(this.rafId);
      this.rafId = 0;
    }

    this.detachCanvasEvents();
    this.boundWrap = null;
  },

  methods: {
    setupAuthAndLoad() {
      if (this.userId) {
        this.resolvedUid = this.userId;
        this.fetchDashboard(this.userId);
        return;
      }

      const auth = getAuth();

      this.authUnsub = onAuthStateChanged(auth, (user) => {
        const uid = user?.uid || "";

        if (!uid) {
          this.resolvedUid = "";
          this.$store.commit("groupDashboardStats/clearStats");
          this.$store.commit(
            "groupDashboardStats/setError",
            "No hay ID de usuario disponible."
          );
          return;
        }

        if (uid !== this.resolvedUid) {
          this.resolvedUid = uid;
          this.fetchDashboard(uid);
        }
      });
    },

    async fetchDashboard(uid) {
      if (!uid) {
        this.$store.commit(
          "groupDashboardStats/setError",
          "No hay ID de usuario disponible."
        );
        return;
      }

      try {
        await this.$store.dispatch("groupDashboardStats/fetch", {
          userId: uid,
          month: this.month || undefined
        });
      } finally {
        this.$nextTick(() => {
          this.setupCanvasBindings();
          this.scheduleDraw();
        });
      }
    },

    setupCanvasBindings() {
      const wrap = this.$refs.compareWrap || null;
      const canvas = this.$refs.compareCanvas || null;

      if (this.boundCanvas && this.boundCanvas !== canvas) {
        this.detachCanvasEvents(this.boundCanvas);
      }

      if (canvas && this.boundCanvas !== canvas) {
        canvas.addEventListener("mousemove", this.onCanvasMove);
        canvas.addEventListener("mouseleave", this.onCanvasLeave);
        canvas.addEventListener("touchstart", this.onTouchMove, { passive: true });
        canvas.addEventListener("touchmove", this.onTouchMove, { passive: true });
        canvas.addEventListener("touchend", this.onCanvasLeave, { passive: true });
        canvas.addEventListener("touchcancel", this.onCanvasLeave, { passive: true });
      }

      this.boundCanvas = canvas;

      if (this.ro && this.boundWrap !== wrap) {
        this.ro.disconnect();
        this.ro = null;
      }

      if (
        typeof window !== "undefined" &&
        window.ResizeObserver &&
        wrap &&
        this.boundWrap !== wrap
      ) {
        this.ro = new ResizeObserver(() => {
          this.scheduleDraw();
        });
        this.ro.observe(wrap);
      }

      this.boundWrap = wrap || null;
    },

    detachCanvasEvents(canvas = this.boundCanvas) {
      if (!canvas) return;

      canvas.removeEventListener("mousemove", this.onCanvasMove);
      canvas.removeEventListener("mouseleave", this.onCanvasLeave);
      canvas.removeEventListener("touchstart", this.onTouchMove);
      canvas.removeEventListener("touchmove", this.onTouchMove);
      canvas.removeEventListener("touchend", this.onCanvasLeave);
      canvas.removeEventListener("touchcancel", this.onCanvasLeave);

      if (canvas === this.boundCanvas) {
        this.boundCanvas = null;
      }
    },

    scheduleDraw() {
      if (this.rafId || typeof window === "undefined") return;

      this.rafId = window.requestAnimationFrame(() => {
        this.rafId = 0;
        this.drawCompareChart();
      });
    },

    toggleCat(key) {
      this.activeCat = key;
    },

    fmtMs(ms = 0) {
      const totalSeconds = Math.floor(Number(ms || 0) / 1000);
      const h = Math.floor(totalSeconds / 3600);
      const m = Math.floor((totalSeconds % 3600) / 60);
      const s = totalSeconds % 60;
      const pad = (n) => String(n).padStart(2, "0");
      return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
    },

    initials(name = "") {
      const parts = String(name)
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2);

      if (!parts.length) return "—";
      return parts.map((p) => p[0]?.toUpperCase() || "").join("");
    },

    studentMeta(student) {
      return `${student.activeDays || 0} días activos`;
    },

    inferCategory(lvl) {
      const raw = (lvl?.category || lvl?.cat || lvl?.group || "").toString().toLowerCase();
      const title = (lvl?.title || "").toString().toLowerCase();
      const id = (lvl?.id || "").toString().toLowerCase();
      const levelId = (lvl?.levelId || "").toString().toLowerCase();
      const tags = (lvl?.tags || []).map((t) => String(t).toLowerCase());

      const has = (...kw) => {
        const pool = [raw, title, id, levelId, ...tags];
        return kw.some((k) => pool.some((p) => p.includes(k)));
      };

      if (has("letters", "letter", "letra", "letras", "abc")) {
        return { key: "letters", title: "Letras", emoji: "🔤" };
      }

      if (has("numbers", "number", "numero", "número", "numeros", "números", "123")) {
        return { key: "numbers", title: "Números", emoji: "🔢" };
      }

      if (has("body", "cuerpo")) {
        return { key: "body", title: "Cuerpo", emoji: "🧍" };
      }

      if (has("fruits", "fruit", "veggies", "vegetables", "verduras", "frutas", "food", "comida")) {
        return { key: "food", title: "Comida", emoji: "🍎" };
      }

      if (has("animals", "animal", "zoo")) {
        return { key: "animals", title: "Animales", emoji: "🐾" };
      }

      if (has("colors", "color", "colores")) {
        return { key: "colors", title: "Colores", emoji: "🎨" };
      }

      if (has("shapes", "shape", "formas", "forma")) {
        return { key: "shapes", title: "Formas", emoji: "🟦" };
      }

      if (has("transport", "vehicle", "vehiculo", "vehículo", "cars", "car")) {
        return { key: "transport", title: "Transporte", emoji: "🚗" };
      }

      if (has("professions", "profession", "profesion", "profesión", "oficio")) {
        return { key: "professions", title: "Profesiones", emoji: "👩‍🏫" };
      }

      return { key: "others", title: "Otros", emoji: "✨" };
    },

    fitText(ctx, text, maxWidth) {
      const value = String(text || "");
      if (!value) return "";
      if (ctx.measureText(value).width <= maxWidth) return value;

      let out = value;
      while (out.length > 1 && ctx.measureText(`${out}…`).width > maxWidth) {
        out = out.slice(0, -1);
      }
      return `${out}…`;
    },

    roundedRect(ctx, x, y, w, h, r) {
      if (typeof ctx.roundRect === "function") {
        ctx.beginPath();
        ctx.roundRect(x, y, w, h, r);
        return;
      }

      const radius = Math.min(r, w / 2, h / 2);
      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.arcTo(x + w, y, x + w, y + h, radius);
      ctx.arcTo(x + w, y + h, x, y + h, radius);
      ctx.arcTo(x, y + h, x, y, radius);
      ctx.arcTo(x, y, x + w, y, radius);
      ctx.closePath();
    },

    onCanvasMove(e) {
      const canvas = this.$refs.compareCanvas;
      if (!canvas || !this.bars.length) return;

      const rect = canvas.getBoundingClientRect();
      const x = (e.clientX - rect.left) * this.dpr;
      const y = (e.clientY - rect.top) * this.dpr;

      const idx = this.bars.findIndex(
        (bar) =>
          x >= bar.x &&
          x <= bar.x + bar.w &&
          y >= bar.y &&
          y <= bar.y + bar.h
      );

      this.hoverIndex = idx;
      this.drawCompareChart();
    },

    onTouchMove(e) {
      const touch = e.touches?.[0];
      if (!touch) return;

      const canvas = this.$refs.compareCanvas;
      if (!canvas || !this.bars.length) return;

      const rect = canvas.getBoundingClientRect();
      const y = (touch.clientY - rect.top) * this.dpr;

      let best = { i: -1, dist: Infinity };

      this.bars.forEach((bar, i) => {
        const center = bar.y + bar.h / 2;
        const dist = Math.abs(center - y);
        if (dist < best.dist) best = { i, dist };
      });

      this.hoverIndex = best.i;
      this.drawCompareChart();
    },

    onCanvasLeave() {
      this.hoverIndex = -1;
      this.drawCompareChart();
    },

    drawCompareChart() {
      const canvas = this.$refs.compareCanvas;
      if (!canvas) return;

      const wrap = this.$refs.compareWrap || canvas.parentElement;
      const wrapW = Math.max(280, Math.min(980, wrap?.clientWidth || 640));

      const rows = Array.isArray(this.chartRows) ? this.chartRows : [];
      if (!rows.length) {
        const fallbackH = 220;
        canvas.width = Math.round(wrapW * this.dpr);
        canvas.height = Math.round(fallbackH * this.dpr);
        canvas.style.width = `${wrapW}px`;
        canvas.style.height = `${fallbackH}px`;

        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }

        this.bars = [];
        return;
      }

      const rowHeightCss = wrapW < 520 ? 40 : 46;
      const rectH = Math.max(220, Math.min(720, rows.length * rowHeightCss + 70));

      const targetW = Math.round(wrapW * this.dpr);
      const targetH = Math.round(rectH * this.dpr);

      if (canvas.width !== targetW) canvas.width = targetW;
      if (canvas.height !== targetH) canvas.height = targetH;
      if (canvas.style.width !== `${wrapW}px`) canvas.style.width = `${wrapW}px`;
      if (canvas.style.height !== `${rectH}px`) canvas.style.height = `${rectH}px`;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const W = canvas.width;
      const H = canvas.height;

      ctx.clearRect(0, 0, W, H);

      const padL = (wrapW < 520 ? 98 : 140) * this.dpr;
      const padR = 44 * this.dpr;
      const padT = 20 * this.dpr;
      const padB = 22 * this.dpr;

      const plotW = Math.max(80 * this.dpr, W - padL - padR);
      const plotH = Math.max(80 * this.dpr, H - padT - padB);

      const values = rows.map((r) => Number(r.value || 0));
      const maxV = Math.max(1, ...values);

      const niceStep = (m) => {
        if (m <= 4) return 1;
        if (m <= 10) return 2;
        if (m <= 20) return 5;
        if (m <= 50) return 10;
        if (m <= 100) return 20;
        if (m <= 250) return 50;
        return Math.ceil(m / 6);
      };

      const stepVal = niceStep(maxV);
      const maxTickVal = Math.ceil(maxV / stepVal) * stepVal || 1;

      const rowGap = plotH / rows.length;
      const barH = Math.min(22 * this.dpr, rowGap * 0.58);

      const fontBase = 12 * this.dpr;
      ctx.font = `${fontBase}px ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial`;
      ctx.textBaseline = "middle";

      ctx.strokeStyle = "rgba(0,0,0,0.08)";
      ctx.lineWidth = 1 * this.dpr;
      ctx.fillStyle = "rgba(0,0,0,0.55)";
      ctx.textAlign = "center";

      for (let v = 0; v <= maxTickVal; v += stepVal) {
        const x = padL + (v / maxTickVal) * plotW;

        ctx.beginPath();
        ctx.moveTo(x, padT);
        ctx.lineTo(x, H - padB);
        ctx.stroke();

        ctx.fillText(String(v), x, H - padB + 12 * this.dpr);
      }

      this.bars = [];

      rows.forEach((row, i) => {
        const centerY = padT + rowGap * i + rowGap / 2;
        const y = centerY - barH / 2;
        const w = Math.max(row.value > 0 ? 2 * this.dpr : 0, (row.value / maxTickVal) * plotW);

        const isHover = i === this.hoverIndex;
        const barColor = isHover ? "#22c7e8" : "#38D5F4";
        const trackColor = "rgba(45,41,73,0.07)";

        ctx.fillStyle = trackColor;
        this.roundedRect(ctx, padL, y, plotW, barH, 999 * this.dpr);
        ctx.fill();

        ctx.fillStyle = barColor;
        this.roundedRect(ctx, padL, y, w, barH, 999 * this.dpr);
        ctx.fill();

        ctx.fillStyle = "#2d2949";
        ctx.textAlign = "right";
        const fittedName = this.fitText(ctx, row.label, padL - 14 * this.dpr);
        ctx.fillText(fittedName, padL - 10 * this.dpr, centerY);

        ctx.textAlign = "left";
        ctx.fillStyle = "rgba(45,41,73,0.82)";
        ctx.fillText(String(row.value), Math.min(padL + w + 8 * this.dpr, W - padR), centerY);

        this.bars.push({
          x: padL,
          y,
          w: Math.max(w, 8 * this.dpr),
          h: barH,
          centerY,
          label: row.label,
          value: row.value,
          sessions: row.sessions,
          completed: row.completed,
          activeDays: row.activeDays,
          totalMs: row.totalMs,
          kidIds: row.kidIds
        });
      });

      if (this.hoverIndex >= 0 && this.bars[this.hoverIndex]) {
        const bar = this.bars[this.hoverIndex];

        const line1 = bar.label;
        const line2 = `Completadas: ${bar.completed}`;
        const line3 = `Sesiones: ${bar.sessions}`;
        const line4 = `Tiempo: ${this.fmtMs(bar.totalMs)}`;

        ctx.font = `${fontBase + 1}px ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial`;

        const pad = 8 * this.dpr;
        const lines = [line1, line2, line3, line4];
        const boxW =
          Math.max(...lines.map((line) => ctx.measureText(line).width)) + pad * 2;
        const lineH = fontBase + 6 * this.dpr;
        const boxH = lineH * lines.length + pad;

        let tx = Math.min(padL + bar.w + 14 * this.dpr, W - padR - boxW);
        let ty = bar.y - boxH / 2 + bar.h / 2;

        if (ty < padT) ty = padT;
        if (ty + boxH > H - padB) ty = H - padB - boxH;

        ctx.fillStyle = "rgba(45, 41, 73, 0.96)";
        this.roundedRect(ctx, tx, ty, boxW, boxH, 10 * this.dpr);
        ctx.fill();

        ctx.fillStyle = "#ffffff";
        ctx.textAlign = "left";
        ctx.textBaseline = "top";

        lines.forEach((line, idx) => {
          ctx.fillText(line, tx + pad, ty + pad / 1.5 + idx * lineH);
        });
      }
    }
  }
};
</script>

<style scoped>
.dashboard {
  --bg: #f5f7fb;
  --ink: #2d2949;
  --muted: #6b6b6b;
  --card-bg: #ffffff;
  --soft: #f8f7ff;
  --inset: #e9e7f5;
  --accent: #38d5f4;

  position: relative;
  width: 100%;
  min-height: 80vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 20px 0 40px;
  overflow: hidden;
  background:
    radial-gradient(1200px 400px at 80% -10%, rgba(56, 213, 244, 0.08), transparent 60%),
    radial-gradient(1000px 380px at -10% 0%, rgba(128, 106, 255, 0.06), transparent 60%),
    var(--bg);
}

.message {
  font-size: clamp(1.6rem, 2.2vw + 1rem, 2.2rem);
  font-weight: 800;
  text-align: center;
  margin: 8px 0 4px;
  z-index: 12;
  color: var(--ink);
  letter-spacing: -0.02em;
}

.submessage {
  margin: 0 0 14px;
  color: var(--muted);
  text-align: center;
  font-size: 14px;
}

.cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
  width: min(980px, 94vw);
  margin-top: 8px;
  z-index: 5;
}

@media (max-width: 620px) {
  .cards {
    grid-template-columns: 1fr;
  }
}

.card {
  background: var(--card-bg);
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(43, 31, 86, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04);
  padding: 14px 16px;
  transition: transform 140ms ease, box-shadow 140ms ease;
  border: 1px solid rgba(45, 41, 73, 0.06);
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(43, 31, 86, 0.12), 0 4px 10px rgba(0, 0, 0, 0.06);
}

.card-feature {
  background:
    radial-gradient(100% 120% at 100% 0%, rgba(56, 213, 244, 0.12), transparent 55%),
    #ffffff;
}

.card-title {
  font-weight: 700;
  color: var(--ink);
  margin-bottom: 8px;
}

.feature-main {
  font-size: 1.15rem;
  font-weight: 800;
  color: var(--ink);
  line-height: 1.25;
}

.feature-sub {
  margin-top: 6px;
  color: var(--muted);
  font-size: 13px;
}

.kpis {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.kpis.single {
  grid-template-columns: 1fr;
}

.kpi {
  background: var(--soft);
  border-radius: 12px;
  padding: 10px 12px;
  text-align: center;
  box-shadow: inset 0 0 0 1px var(--inset);
}

.kpi span {
  display: block;
  font-size: 12px;
  color: var(--muted);
}

.kpi b {
  font-size: clamp(18px, 1.5vw + 12px, 20px);
  color: var(--ink);
  letter-spacing: -0.01em;
}

.sections {
  width: min(980px, 94vw);
  margin: 18px auto 24px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
}

.section {
  background: var(--card-bg);
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(43, 31, 86, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04);
  padding: 14px 16px;
  border: 1px solid rgba(45, 41, 73, 0.06);
}

.section-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px 12px;
  flex-wrap: wrap;
}

.section-title {
  margin: 0 0 10px;
  color: var(--ink);
}

.legend {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--muted);
  font-size: 13px;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  display: inline-block;
}

.dot-completed {
  background: #38d5f4;
}

.compare-wrap {
  width: 100%;
  display: block;
  overflow: hidden;
  padding: 2px 0;
}

.compare-canvas {
  width: 100%;
  height: auto;
  display: block;
  touch-action: pan-x pan-y;
  max-width: 100%;
}

.skeleton-chart {
  margin-top: 8px;
  width: 100%;
  height: 16px;
  border-radius: 8px;
  background: linear-gradient(90deg, #f0eefc, #e6e3fb, #f0eefc);
  background-size: 200% 100%;
  animation: shimmer 1.2s infinite;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.students-list {
  list-style: none;
  padding: 0;
  margin: 4px 0 0;
  display: grid;
  gap: 10px;
}

.student-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px 16px;
  background: var(--soft);
  border-radius: 12px;
  padding: 12px;
  box-shadow: inset 0 0 0 1px var(--inset);
}

.student-main {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.student-avatar {
  width: 42px;
  height: 42px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, rgba(56, 213, 244, 0.16), rgba(128, 106, 255, 0.14));
  color: var(--ink);
  font-weight: 800;
  flex: 0 0 auto;
}

.student-info {
  min-width: 0;
}

.student-name {
  font-weight: 800;
  color: var(--ink);
  word-break: break-word;
}

.student-kpis {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
  justify-content: flex-end;
  color: #444;
  font-size: 14px;
}

.empty-block {
  color: var(--muted);
  background: #faf9ff;
  border: 1px dashed #ddd7f2;
  border-radius: 12px;
  padding: 14px;
  margin-top: 4px;
}

.cats-row {
  --cat-size: 170px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin: 8px 0 14px;
}

.cat-chip {
  display: grid;
  grid-template-rows: auto auto auto;
  grid-template-columns: 1fr;
  justify-items: center;
  align-content: center;
  gap: 4px;
  padding: 12px;
  border-radius: 14px;
  background: var(--soft);
  box-shadow: inset 0 0 0 1px var(--inset);
  color: var(--ink);
  border: none;
  text-align: center;
  cursor: pointer;
  transition: background 140ms ease, transform 140ms ease, box-shadow 140ms ease;
  aspect-ratio: 1 / 1;
  width: var(--cat-size);
  min-width: var(--cat-size);
  max-width: var(--cat-size);
  flex: 0 0 var(--cat-size);
}

.cat-chip:hover {
  transform: translateY(-1px);
  background: #f3f1ff;
}

.cat-chip.active {
  box-shadow: inset 0 0 0 2px var(--accent), 0 6px 18px rgba(56, 213, 244, 0.18);
  background: #eefcff;
}

@media (max-width: 640px) {
  .cats-row {
    --cat-size: min(42vw, 170px);
  }
}

@media (max-width: 380px) {
  .cats-row {
    --cat-size: min(78vw, 170px);
  }
}

.cat-emoji {
  font-size: clamp(22px, 2.2vw + 14px, 28px);
  line-height: 1;
}

.cat-emoji.big {
  font-size: 26px;
}

.cat-title {
  font-weight: 700;
}

.cat-metrics {
  font-size: 12px;
  color: var(--muted);
}

.category-block {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.category-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 12px;
  padding: 8px 10px;
  background: #faf9ff;
  border-radius: 12px;
  box-shadow: inset 0 0 0 1px #eeeafc;
}

.category-name {
  display: flex;
  align-items: center;
  gap: 8px;
}

.category-name h4 {
  margin: 0;
  font-size: 16px;
  color: var(--ink);
}

.category-totals {
  display: flex;
  gap: 12px;
  color: #444;
  font-size: 14px;
  flex-wrap: wrap;
}

.levels {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 10px;
}

.level-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--soft);
  border-radius: 12px;
  padding: 10px 12px;
  box-shadow: inset 0 0 0 1px var(--inset);
  transition: background 140ms ease, transform 140ms ease;
}

.level-row:hover {
  background: #f3f1ff;
  transform: translateY(-1px);
}

.level-title {
  font-weight: 700;
  color: var(--ink);
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.muted {
  color: var(--muted);
  font-weight: 400;
}

.level-kpis {
  display: flex;
  gap: 12px;
  color: #444;
  font-size: 14px;
  flex-wrap: wrap;
}

.empty-state {
  width: min(980px, 94vw);
  margin-top: 8px;
  color: var(--muted);
  background: #faf9ff;
  border: 1px dashed #ddd7f2;
  padding: 12px 14px;
  border-radius: 12px;
}

.error {
  width: min(980px, 94vw);
  margin-top: 16px;
  color: #b00020;
  background: #fde7eb;
  border: 1px solid #f7c6d1;
  padding: 10px 12px;
  border-radius: 10px;
}

@media (max-width: 900px) {
  .student-row {
    display: block;
  }

  .student-main {
    margin-bottom: 8px;
  }

  .student-kpis {
    justify-content: flex-start;
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .category-head {
    flex-direction: column;
    align-items: flex-start;
  }

  .category-totals {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px;
    text-align: left;
  }

  .category-totals span {
    display: block;
  }

  .level-row {
    display: block;
    align-items: flex-start;
  }

  .level-title {
    width: 100%;
    margin-bottom: 6px;
  }

  .level-kpis {
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px;
    width: 100%;
    margin-top: 0;
    justify-items: start;
    text-align: left;
  }

  .level-kpis span {
    display: block;
  }
}
</style>